import React, { useContext } from 'react';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type { Theme} from '@mui/material';
import { Typography, useTheme } from '@mui/material';
import type DocumentNode from '../../Models/DocumentNodeModel';
import fileDownload from 'js-file-download';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';

type DocumentInfoDialogProps = {
    controller: boolean,
    onClose: () => void,
    documentNode: DocumentNode | null,
};

function DocumentInfoDialog(props: Readonly<DocumentInfoDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    
    const currentTheme: Theme = useTheme();
    
    async function handleDownloadFile(): Promise<void> {

        if (props.documentNode === null) {
            return;
        }

        const fileBlob: Blob | null = await indexedDBContext.indexedDBService.getDocumentBlob(props.documentNode.documentId);

        if (fileBlob === null) {
            return;
        }

        fileDownload(fileBlob, props.documentNode.fileName);
    }

    function handleClose(): void {

        updateLastActivity(userContext, localStorageContext);

        props.onClose();
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose()}
        >
            {props.documentNode === null //should not happen
                ? (
                    <DialogTitle>No Document Selected</DialogTitle>
                )
                : (
                    <>
                        <DialogTitle>{props.documentNode.name}</DialogTitle>
                        <DialogContent>
                            <table 
                                cellPadding={10}
                                style={{
                                    textAlign: 'center',
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td>
                                            <Typography
                                                style={{
                                                    color: currentTheme.palette.text.primary,
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                File Name
                                            </Typography>
                                            <Typography
                                                style={{
                                                    color: currentTheme.palette.text.primary,
                                                }}
                                            >
                                                {props.documentNode.fileName}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                style={{
                                                    color: currentTheme.palette.text.primary,
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                Revision No.
                                            </Typography>
                                            <Typography
                                                style={{
                                                    color: currentTheme.palette.text.primary,
                                                }}
                                            >
                                                {props.documentNode.revisionNo}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <Button 
                                                variant='contained'
                                                color='primary'
                                                onClick={handleDownloadFile}
                                                style={{
                                                    minWidth: '90px',
                                                    maxWidth: '90px',
                                                    minHeight: '40px',
                                                    maxHeight: '40px',
                                                }}
                                            >
                                                Open
                                            </Button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </DialogContent>
                    </>
                )
            }
            <DialogActions>
                <Button
                    onClick={(): void => handleClose()}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DocumentInfoDialog;